import * as Yup from 'yup';
import { PupilValues } from './CustomerDetailsForm';
import { YesNoOptions } from '../../redux/user-selection/userSelection';

const specialCharRegex = /^[a-zA-Z0-9\s\']*$/;
const specialCharErrorMesg = 'Only characters A-Z and 0-9 allowed';

export const firstNameErrorMessages = {
    required: 'First name is required',
    max: 'A maximum of 50 characters allowed'
};

export const firstNameValidation = Yup.string()
    .trim()
    .required(firstNameErrorMessages.required)
    .matches(specialCharRegex, specialCharErrorMesg)
    .max(50, firstNameErrorMessages.max);

export const surnameErrorMessages = {
    required: 'Surname is required',
    max: 'A maximum of 50 characters allowed'
};

export const surnameValidation = Yup.string()
    .trim()
    .required(surnameErrorMessages.required)
    .matches(specialCharRegex, specialCharErrorMesg)
    .max(50, surnameErrorMessages.max);

export const dobErrorMessages = {
    required: 'Date of birth is required',
    type: 'Invalid date'
};

export const dobValidation = Yup.date()
    .transform((value, originalValue) => {
        const dobYear = String(originalValue?.dobYear || '');
        const dobMonth = String(originalValue?.dobMonth || '');
        const dobDay = String(originalValue?.dobDay || '');
        const hasValidFormat = /^\d{4}$/.test(dobYear)
            && /^\d{1,2}$/.test(dobMonth)
            && /^\d{1,2}$/.test(dobDay);

        if (!hasValidFormat) {
            return new Date('');
        }

        const year = Number(dobYear);
        const month = Number(dobMonth);
        const day = Number(dobDay);
        const date = new Date(0);
        date.setUTCHours(0, 0, 0, 0);
        date.setUTCFullYear(year, month - 1, day);

        return date.getUTCFullYear() === year
            && date.getUTCMonth() === month - 1
            && date.getUTCDate() === day
            ? date
            : new Date('');
    })
    .required(dobErrorMessages.required)
    .typeError(dobErrorMessages.type);

export const addressLine1ErrorMessages = {
    required: 'House name / number is required',
    max: 'A maximum of 100 characters allowed'
};

export const addressLine1Validation = Yup.string()
    .trim()
    .required(addressLine1ErrorMessages.required)
    .matches(specialCharRegex, specialCharErrorMesg)
    .max(100, addressLine1ErrorMessages.max);

export const addressLine2ErrorMessages = {
    required: 'Street name is required',
    max: 'Street name is too long'
};

export const addressLine2Validation = Yup.string()
    .trim()
    .required(addressLine2ErrorMessages.required)
    .matches(specialCharRegex, specialCharErrorMesg)
    .max(100, addressLine2ErrorMessages.max);

export const townErrorMessages = {
    required: 'Town is required',
    max: 'A maximum of 50 characters allowed'
};
export const townValidation = Yup.string()
    .trim()
    .required(townErrorMessages.required)
    .matches(specialCharRegex, specialCharErrorMesg)
    .max(50, townErrorMessages.max);

export const countyIdErrorMessages = {
    type: 'County is required',
    required: 'County is required'
};

export const countyIdValidation = Yup.number()
    .typeError(countyIdErrorMessages.type)
    .required(countyIdErrorMessages.required);

export const mobileErrorMessages = {
    required: 'Mobile phone number is required',
    number: 'Mobile phone number can only by numeric digits',
    max: 'A maximum of 20 characters allowed'
};

export const mobileValidation = Yup.string()
    .trim()
    .required(mobileErrorMessages.required)
    .matches(/^\d+$/, mobileErrorMessages.number)
    .max(20, mobileErrorMessages.max);

export const emergencyNumErrorMessages = {
    required: 'Emergency mobile phone number is required',
    number: 'Emergency mobile phone number can only by numeric digits',
    max: 'A maximum of 20 characters allowed',
    same: 'Phone numbers cannot be the same'
};

export const emergencyMobileValidation = Yup.string()
    .trim()
    .required(emergencyNumErrorMessages.required)
    .matches(/^\d+$/, emergencyNumErrorMessages.number)
    .max(20, emergencyNumErrorMessages.max)
    .test(
        'emergency-number-differnece',
        emergencyNumErrorMessages.same,
        function (this: { parent: PupilValues }, value?: string) {
            const mobile = this.parent.mobile;
            return value !== mobile;
        });

export const emailErrorMessages = {
    required: 'Email is required',
    email: 'Invalid email address',
    max: 'A maximum of 50 characters allowed'
};

export const emailValidation = Yup.string()
    .trim()
    .required(emailErrorMessages.required)
    .email(emailErrorMessages.email)
    .max(50, emailErrorMessages.max);

export const confirmEmailErrorMessages = {
    required: 'Please confirm your email',
    confirmEmail: 'Email must be the same'
};
export const confirmEmailValidation = Yup.string()
    .trim()
    .required(confirmEmailErrorMessages.required)
    .test(
        'confirm-email',
        confirmEmailErrorMessages.confirmEmail,
        function (this: { parent: PupilValues }, value?: string) {
            const email = this.parent.email;
            return value == null || value === email;
        });

export const eircodeErrorMessages = {
    validity: 'Invalid Eircode'
};

export const eircodeValidation = Yup.string()
    // tslint:disable-next-line: max-line-length
    .matches(/(?:^[AC-FHKNPRTV-Yac-fhknprtv-y][0-9]{2}|D6W)[ -]?[0-9AC-FHKNPRTV-Yac-fhknprtv-y]{4}[ -]*$|^$/, eircodeErrorMessages.validity);
    
export const PupilValidationSchema = Yup.object().shape({
    firstName: firstNameValidation,
    surname: surnameValidation,
    dob: dobValidation,
    addressLine1: addressLine1Validation,
    addressLine2: addressLine2Validation,
    town: townValidation,
    countyId: countyIdValidation,
    mobile: mobileValidation,
    emergencyNumber: emergencyMobileValidation,
    email: emailValidation,
    confirmEmail: confirmEmailValidation,
});

export const thirdPartyValidationSchema = Yup.object().shape({
    firstName: firstNameValidation,
    surname: surnameValidation,
    addressLine1: addressLine1Validation,
    addressLine2: addressLine2Validation,
    town: townValidation,
    countyId: countyIdValidation,
    eircode: eircodeValidation,
    mobile: mobileValidation,
    email: emailValidation,
    confirmEmail: confirmEmailValidation,
});

export const validationSchema =
    Yup.object().shape({
        sameDetails: Yup.number().notRequired(),
        thirdParty: Yup.object().when('sameDetails', {
            is: val => val === YesNoOptions.No,
            then: thirdPartyValidationSchema,
            otherwise: Yup.object().notRequired(),
        }),
        pupil: PupilValidationSchema,
    });
